<template>
  <v-card variant="outlined" class="telegram-webhook-card mt-4">
    <div class="d-flex align-center ga-3 pa-3 flex-wrap">
      <v-icon size="20">mdi-webhook</v-icon>
      <div>
        <div class="text-subtitle-2 font-weight-medium">
          {{ $t("ncrm.integrations.telegram.webhook.title") }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.integrations.telegram.webhook.subtitle") }}
        </div>
      </div>
      <v-spacer />
      <v-chip
        :color="statusColor"
      >
        {{ $t("ncrm.integrations.telegram.webhook.status." + (account.webhook_status || "none")) }}
      </v-chip>
    </div>
    <v-divider />
    <div class="pa-3">
      <div v-if="account.webhook_url" class="mb-2">
        <span class="text-caption text-medium-emphasis mr-1">URL:</span>
        <code class="text-caption">{{ account.webhook_url }}</code>
      </div>
      <v-alert
        v-if="account.webhook_last_error"
        type="error"
        density="compact"
        class="mb-2"
      >
        {{ account.webhook_last_error }}
      </v-alert>
      <div class="d-flex ga-2 flex-wrap">
        <v-btn
          color="primary"
          variant="tonal"
          size="small"
          :loading="busy === 'setup'"
          @click="setupWebhook"
        >
          <v-icon start size="16">mdi-link-plus</v-icon>
          {{
            account.webhook_status === "active"
              ? $t("ncrm.integrations.telegram.webhook.actions.rotate")
              : $t("ncrm.integrations.telegram.webhook.actions.setup")
          }}
        </v-btn>
        <v-btn
          v-if="account.webhook_status === 'active'"
          color="error"
          variant="text"
          size="small"
          :loading="busy === 'disable'"
          @click="disableWebhook"
        >
          {{ $t("ncrm.integrations.telegram.webhook.actions.disable") }}
        </v-btn>
        <v-btn
          variant="text"
          size="small"
          :loading="busy === 'status'"
          @click="refreshStatus"
        >
          <v-icon start size="16">mdi-refresh</v-icon>
          {{ $t("ncrm.integrations.telegram.webhook.actions.refresh") }}
        </v-btn>
      </div>
      <v-alert
        v-if="errorMessage"
        type="error"
        density="compact"
        class="mt-3"
      >
        {{ errorMessage }}
      </v-alert>
      <v-alert
        v-if="remoteInfo"
        type="info"
        density="compact"
        class="mt-3"
      >
        {{
          $t("ncrm.integrations.telegram.webhook.remoteSummary", {
            url: remoteInfo.url || "—",
            pending: remoteInfo.pending_update_count ?? 0,
          })
        }}
      </v-alert>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";

export default {
  name: "TelegramWebhookCard",
  props: {
    account: { type: Object, required: true },
  },
  emits: ["account-updated", "notify"],
  data() {
    return {
      busy: null,
      errorMessage: "",
      remoteInfo: null,
    };
  },
  computed: {
    statusColor() {
      if (this.account.webhook_status === "active") return "success";
      if (this.account.webhook_status === "error") return "error";
      return "neutral";
    },
  },
  methods: {
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    async setupWebhook() {
      this.busy = "setup";
      this.errorMessage = "";
      try {
        const { data } = await axios.post(
          `/integrations/telegram/accounts/${this.account.id}/webhooks/setup`,
          {}
        );
        if (data?.account) this.$emit("account-updated", data.account);
        this.$emit("notify", {
          message: this.$t("ncrm.integrations.telegram.webhook.snackbar.setupOk"),
          color: "success",
        });
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.webhook.snackbar.setupError"
        );
      } finally {
        this.busy = null;
      }
    },
    async disableWebhook() {
      this.busy = "disable";
      this.errorMessage = "";
      try {
        const { data } = await axios.delete(
          `/integrations/telegram/accounts/${this.account.id}/webhooks`
        );
        if (data?.account) this.$emit("account-updated", data.account);
        if (data?.telegram_ok === false && data?.telegram_error) {
          this.errorMessage = data.telegram_error;
        } else {
          this.$emit("notify", {
            message: this.$t("ncrm.integrations.telegram.webhook.snackbar.disabled"),
            color: "info",
          });
        }
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.webhook.snackbar.disableError"
        );
      } finally {
        this.busy = null;
      }
    },
    async refreshStatus() {
      this.busy = "status";
      this.errorMessage = "";
      this.remoteInfo = null;
      try {
        const { data } = await axios.get(
          `/integrations/telegram/accounts/${this.account.id}/webhooks/status`
        );
        this.remoteInfo = data?.remote || null;
        if (data?.remote_error) {
          this.errorMessage = data.remote_error;
        }
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.webhook.snackbar.statusError"
        );
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>

<style scoped>
.telegram-webhook-card code {
  background: rgba(var(--v-theme-on-surface), 0.06);
  padding: 2px 6px;
  border-radius: 3px;
  word-break: break-all;
}
</style>
